import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Gem, Shield, Crown, RefreshCw, Lightbulb, ArrowLeft, Check, Sparkles, Flame, Palette, MessagesSquare, Tag } from "lucide-react";
import api from "@/lib/api";
import GemIcon from "@/components/GemIcon";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const ITEMS = [
  {
    key: "streak_freeze", icon: Shield, accent: "#5AC8FA",
    name: "Streak Freeze", price: 50,
    pitch: "Miss a day? Burn one and your streak survives.",
    bullets: ["Auto-consumed when you skip a day", "Stockpile up to 2", "Saves your gem rewards"],
    cta: "Buy freeze",
    endpoint: "/gems/redeem/streak-freeze",
  },
  {
    key: "pro_pass", icon: Crown, accent: "#FFD86B",
    name: "24h Pro Pass", price: 300, highlight: true,
    pitch: "Unlock every Pro feature for 24 hours.",
    bullets: ["Audio · Video · YouTube ingestion", "Exam Mode + Tutor Pro", "Smarter AI model"],
    cta: "Activate pass",
    endpoint: "/gems/redeem/pro-pass",
  },
  {
    key: "tutor_pro", icon: MessagesSquare, accent: "#B8A4FF",
    name: "Tutor Pro · 7 days", price: 250,
    pitch: "Upgrade your AI Tutor to advanced reasoning for a week.",
    bullets: ["Deeper Socratic dialogues", "Longer Explainer breakdowns", "More accurate Coach plans"],
    cta: "Activate Tutor Pro",
    endpoint: "/gems/redeem/tutor-pro",
  },
  {
    key: "pack_boost", icon: Flame, accent: "#FCA5A5",
    name: "Pack Boost · 24h", price: 500,
    pitch: "Pin one of your shared packs at the top of /explore.",
    bullets: ["Pulsing Boosted badge on the tile", "Goes live for 24h", "Pairs well with a low price"],
    cta: "Pick a pack",
    needsContext: "pack",
  },
  {
    key: "accent", icon: Palette, accent: "#34D399",
    name: "UI Accent Palette", price: 200,
    pitch: "Unlock a Violet, Aqua or Solar accent — recolours buttons + glows.",
    bullets: ["One-time per palette", "Switch between unlocked palettes for free", "Backs every glow + chip in the UI"],
    cta: "Pick a palette",
    isAccentPicker: true,
  },
  {
    key: "title_unlock", icon: Tag, accent: "#FCD34D",
    name: "Custom Title", price: 120,
    pitch: "Show a short bio next to your name on the leaderboard.",
    bullets: ["Up to 60 chars", "Persists across packs you publish", "First unlock costs gems — edits are free"],
    cta: "Set my title",
    isTitlePicker: true,
  },
  {
    key: "ai_reroll", icon: RefreshCw, accent: "#B8A4FF",
    name: "AI Re-roll", price: 40,
    pitch: "Regenerate a study with smarter AI.",
    bullets: ["Use on any existing study", "Fresh notes, summary, cards, quiz", "Same source material"],
    cta: "Open studies",
    needsContext: "study",
  },
  {
    key: "quiz_hint", icon: Lightbulb, accent: "#7C5CFF",
    name: "Quiz Hint", price: 10,
    pitch: "Eliminate 2 wrong MCQ options mid-quiz.",
    bullets: ["Used inside the quiz runner", "Per-question — buy as many as you need", "Saves a tough quiz"],
    cta: "Use in a quiz",
    needsContext: "quiz",
  },
];

export default function Gems() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [celebrate, setCelebrate] = useState(null);

  const load = async () => {
    const { data } = await api.get("/gems/me");
    setMe(data);
  };
  useEffect(() => { load(); }, []);

  const purchase = async (item) => {
    if (item.needsContext === "study") { nav("/app/studies"); return; }
    if (item.needsContext === "quiz")  { nav("/app/studies"); toast.info("Start any quiz and look for the hint button"); return; }
    if (item.needsContext === "pack")  { nav("/app/my-packs"); toast.info("Boost any of your published packs from here"); return; }

    if (item.isAccentPicker) {
      const accent = window.prompt("Pick an accent: violet, aqua, or solar");
      if (!accent) return;
      const a = accent.trim().toLowerCase();
      if (!["violet", "aqua", "solar"].includes(a)) { toast.error("Unknown palette"); return; }
      setBusyKey(item.key);
      try {
        const { data } = await api.post("/gems/redeem/accent", { accent: a });
        setCelebrate({ item, data });
        await load(); await refresh();
        setTimeout(() => setCelebrate(null), 2400);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't redeem");
      } finally { setBusyKey(null); }
      return;
    }

    if (item.isTitlePicker) {
      const title = window.prompt("Your custom title (max 60 chars)");
      if (!title || title.trim().length < 2) return;
      setBusyKey(item.key);
      try {
        const { data } = await api.post("/gems/redeem/title", { title: title.trim() });
        setCelebrate({ item, data });
        await load(); await refresh();
        setTimeout(() => setCelebrate(null), 2400);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't redeem");
      } finally { setBusyKey(null); }
      return;
    }

    if (!me || me.gems < item.price) { toast.error("Not enough gems"); return; }
    setBusyKey(item.key);
    try {
      const { data } = await api.post(item.endpoint, {});
      setCelebrate({ item, data });
      await load();
      await refresh();
      setTimeout(() => setCelebrate(null), 2400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't redeem");
    } finally {
      setBusyKey(null);
    }
  };

  const proActive = me?.pro_pass_until && new Date(me.pro_pass_until) > new Date();

  return (
    <div className="max-w-[1180px] mx-auto px-5 md:px-8 py-10 md:py-14">
      <button
        onClick={() => nav(-1)}
        className="text-[13px] font-medium text-[#B0AEBC] hover:text-white flex items-center gap-1 mb-8"
        data-testid="gems-back"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-start justify-between flex-wrap gap-4 mb-10"
      >
        <div>
          <div className="eyebrow">Gem Shop</div>
          <h1 className="display-2 mt-2">Spend your <span className="grad-text-3">gems</span></h1>
          <p className="text-[15px] text-[#B0AEBC] mt-3 max-w-lg">
            Earn gems by unlocking achievements. Spend them to protect your streak, taste Pro, or rescue a tough quiz.
          </p>
        </div>
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 22 }}
          className="surface px-6 py-4 inline-flex items-center gap-3.5"
          style={{ boxShadow: "0 0 28px -8px rgba(184,164,255,0.55)" }}
          data-testid="gem-balance"
        >
          <GemIcon size={22} />
          <span className="text-[32px] font-bold tabular-nums text-white">{me?.gems ?? "—"}</span>
        </motion.div>
      </motion.header>

      {/* Active boosts */}
      <div className="flex flex-wrap gap-2 mb-10">
        {(me?.streak_freezes ?? 0) > 0 && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#5AC8FA]/15 border border-[#5AC8FA]/30 text-[12px] font-semibold text-[#5AC8FA]" data-testid="active-freezes">
            <Shield size={12} /> {me.streak_freezes} freeze{me.streak_freezes > 1 ? "s" : ""} stocked
          </div>
        )}
        {proActive && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD86B]/15 border border-[#FFD86B]/40 text-[12px] font-semibold text-[#FFD86B]" data-testid="active-pro-pass" style={{ boxShadow: "0 0 18px -4px rgba(255,216,107,0.65)" }}>
            <Crown size={12} /> Pro Pass · until {new Date(me.pro_pass_until).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {ITEMS.map((it, i) => {
          const Icon = it.icon;
          const canAfford = (me?.gems ?? 0) >= it.price;
          const freezeMaxed = it.key === "streak_freeze" && (me?.streak_freezes ?? 0) >= (me?.max_freezes ?? 2);
          const disabled = !canAfford || freezeMaxed || busyKey === it.key;
          return (
            <motion.div
              key={it.key}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07, duration: 0.55 }}
              whileHover={{ y: -3 }}
              className="surface p-7 relative overflow-hidden"
              style={it.highlight ? {
                borderColor: `${it.accent}44`,
                boxShadow: `0 0 0 1px ${it.accent}33, 0 18px 60px -12px rgba(0,0,0,0.6), 0 0 40px -8px ${it.accent}66`,
              } : {}}
              data-testid={`shop-${it.key}`}
            >
              <div
                className="absolute -top-16 -right-16 w-44 h-44 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${it.accent}44, transparent 65%)`, filter: "blur(22px)" }}
              />
              <div className="relative">
                <div className="flex items-start justify-between gap-3">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${it.accent}33, ${it.accent}15)`,
                      border: `1px solid ${it.accent}55`,
                      boxShadow: `0 0 22px -6px ${it.accent}99`,
                    }}
                  >
                    <Icon size={22} className="text-white" strokeWidth={1.8} />
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06]">
                    <Gem size={12} style={{ color: it.accent }} />
                    <span className="text-[14px] font-bold tabular-nums text-white">{it.price}</span>
                  </div>
                </div>
                <h3 className="text-[19px] font-semibold mt-5 text-white">{it.name}</h3>
                <p className="text-[13.5px] text-[#B0AEBC] mt-1.5">{it.pitch}</p>
                <ul className="mt-4 space-y-2">
                  {it.bullets.map((b, k) => (
                    <li key={k} className="flex items-start gap-2 text-[12.5px] text-[#E4E4E7]">
                      <Check size={12} className="mt-1 shrink-0" style={{ color: it.accent }} strokeWidth={2.5} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => purchase(it)}
                  disabled={disabled}
                  data-testid={`shop-cta-${it.key}`}
                  className={`btn ${it.highlight ? "btn-primary" : "btn-secondary"} w-full mt-6 py-3 text-[14px] ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {busyKey === it.key
                    ? <><Sparkles size={13} className="animate-pulse" /> Working…</>
                    : freezeMaxed
                    ? "Maxed out"
                    : !canAfford && !it.needsContext
                    ? `Need ${it.price - (me?.gems ?? 0)} more gems`
                    : it.cta}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Earn gems hint */}
      <div className="mt-12 surface p-6 text-center" data-testid="earn-gems-hint">
        <p className="text-[13.5px] text-[#B0AEBC]">
          Out of gems? Hit{" "}
          <button onClick={() => nav("/app/awards")} className="text-[#B8A4FF] hover:text-white underline-offset-4 hover:underline">
            achievement milestones
          </button>{" "}
          to earn more — streaks, mastery, perfect quizzes, and 6 secret ones to discover.
        </p>
      </div>

      <AnimatePresence>
        {celebrate && <RedemptionCelebration item={celebrate.item} data={celebrate.data} />}
      </AnimatePresence>
    </div>
  );
}

function RedemptionCelebration({ item, data }) {
  const Icon = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-5 pointer-events-none"
    >
      <motion.div
        initial={{ scale: 0.6, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 20 }}
        transition={{ type: "spring", stiffness: 280, damping: 18 }}
        className="surface px-8 py-7 max-w-[360px] text-center pointer-events-auto"
        style={{ boxShadow: `0 0 60px -8px ${item.accent}80, 0 0 0 1px ${item.accent}44` }}
      >
        <motion.div
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 260, damping: 18 }}
          className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4"
          style={{
            background: `linear-gradient(135deg, ${item.accent}, ${item.accent}88)`,
            boxShadow: `0 0 36px ${item.accent}99, inset 0 1px 0 rgba(255,255,255,0.3)`,
          }}
        >
          <Icon size={28} className="text-white" strokeWidth={2.2} />
        </motion.div>
        <div className="eyebrow" style={{ color: item.accent }}>Redeemed</div>
        <h3 className="text-[20px] font-semibold text-white mt-1.5">{item.name}</h3>
        <p className="text-[13.5px] text-[#B0AEBC] mt-2">{item.pitch}</p>
        <div className="mt-4 inline-flex items-center gap-1.5 text-[13px] text-[#8E8B99]">
          <GemIcon size={12} glow={false} /> {data.gems} left
        </div>
      </motion.div>
    </motion.div>
  );
}
